import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ref, update } from 'firebase/database';
import { database } from '../../../firebase';
import QuickActions from './QuickActions';
import ActivityTimeline from './ActivityTimeline';
import NotesSection from './NotesSection';
import { field, sectionTitle } from './EmployeeDrawerSections';
import EmailComposer from './modals/EmailComposer';
import ScheduleReviewModal from './modals/ScheduleReviewModal';
import ApprovePTOModal from './modals/ApprovePTOModal';
import ManagerRequestModal from './modals/ManagerRequestModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000";

const ManagerEmployeeDrawer = ({ employeeId, employeesRaw, onClose, drawerOpen }) => {
  const { currentUser, employeeContext } = useAuth();
  const { companyId: contextCompanyId, employeeId: managerId, name: managerName } = employeeContext || {};
  const [activities, setActivities] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const notesSectionRef = useRef(null);
  const [activeModal, setActiveModal] = useState(null);
  
  const emp = employeesRaw[employeeId] || {};
  const profile = emp.profile || {};
  const contact = emp.contact || {};
  const lifecycle = emp.lifecycle || {};
  const pto = emp.pto || {};
  const performance = emp.performance || {};
  const meta = emp.meta || {};
  const portal = emp.portal || {};
  const address = contact.address || {};
  const emergencies = Array.isArray(contact.emergencyContacts) ? contact.emergencyContacts : Object.values(contact.emergencyContacts || {});
  const location = profile.location || {};
  const probation = profile.probation || {};
  
  // Use companyID from prop (meta) or context
  const companyUID = meta.companyUID || contextCompanyId || '';

  // Fetch activities and notes when drawer opens
  useEffect(() => {
    if (employeeId && companyUID) {
      fetchActivities();
      fetchNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, companyUID]);

  const fetchActivities = async () => {
    setLoadingActivities(true);
    try {
      const response = await fetch(`${BACKEND_URL}/employee/activities?companyUID=${companyUID}&employeeId=${employeeId}&limit=50`);
      const data = await response.json();
      if (data.success && data.activities) {
        let activitiesArray = Array.isArray(data.activities) ? data.activities : Object.entries(data.activities || {}).map(([id, activity]) => ({
          id,
          ...activity
        }));
        setActivities(activitiesArray);
      } else {
        setActivities([]);
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const fetchNotes = async () => {
    setLoadingNotes(true);
    try {
      const response = await fetch(`${BACKEND_URL}/employee/notes?companyUID=${companyUID}&employeeId=${employeeId}`);
      const data = await response.json();
      if (data.success && data.notes) {
        let notesArray = Array.isArray(data.notes) ? data.notes : Object.entries(data.notes || {}).map(([id, note]) => ({
          id,
          ...note
        }));
        const sortedNotes = [...notesArray].sort((a, b) => {
          const tsA = a.timestamp || 0;
          const tsB = b.timestamp || 0;
          return tsA - tsB; // Ascending order
        });
        setNotes(sortedNotes);
      } else {
        setNotes([]);
      }
    } catch (error) {
      console.error('Failed to fetch notes:', error);
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleQuickAction = (action) => {
    if (action === 'add-note') {
      if (notesSectionRef.current) {
        notesSectionRef.current.focus();
      }
    } else {
      setActiveModal(action);
    }
  };

  const handleModalClose = () => {
    setActiveModal(null);
  };

  const handleModalSuccess = () => {
    fetchActivities();
    fetchNotes();
  };

  const pendingPTOCount = Object.values(pto.requests || {}).filter(req => req.status === 'pending').length;

  // Status determination
  const lifecycleStatus = (lifecycle.status || emp.status || '').toLowerCase();
  let employeeStatus = 'Active';
  let statusColor = '#40EB6E';
  
  if (lifecycleStatus === 'offboarded' || lifecycleStatus === 'terminated') {
    employeeStatus = 'Offboarded';
    statusColor = '#EB4040';
  } else if (lifecycleStatus === 'on_leave' || lifecycleStatus === 'leave') {
    employeeStatus = 'On Leave / PTO';
    statusColor = '#CC40EB';
  } else if (probation.inProbation) {
    employeeStatus = 'On Probation';
    statusColor = '#EB8D40';
  } else if (lifecycleStatus === 'onboarding') {
    employeeStatus = 'Onboarding';
    statusColor = '#EBDD40';
  }

  return (
    <>
      {/* Base layer: Dark blue background, 50% width, right side */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: drawerOpen ? 0 : '-50%',
          width: '50%',
          height: '100vh',
          backgroundColor: '#031128',
          zIndex: 2000,
          transition: 'right 250ms ease',
          willChange: 'transform',
        }}
      />
      
      {/* Content layer: Sidebar-styled over the base */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: drawerOpen ? 0 : '-50%',
          width: '50%',
          height: '100vh',
          backgroundColor: 'rgba(86, 141, 237, 0.05)',
          borderLeft: '1px solid rgba(86, 141, 237, 0.10)',
          zIndex: 2001,
          transition: 'right 250ms ease',
          overflowY: 'auto',
          padding: '40px',
          boxSizing: 'border-box',
          willChange: 'transform',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            width: 40,
            height: 40,
            backgroundColor: 'rgba(86, 141, 237, 0.1)',
            border: '1px solid rgba(86, 141, 237, 0.2)',
            borderRadius: '8px',
            color: '#FFFFFF',
            fontSize: '20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(86, 141, 237, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgba(86, 141, 237, 0.1)';
          }}
        >
          ×
        </button>

        {/* Profile Section */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ 
            display: 'flex',
            alignItems: 'flex-start',
            gap: 24,
            marginBottom: 24
          }}>
            <div style={{ position: 'relative' }}>
              {/* Decorative frame */}
              <div style={{
                position: 'absolute',
                top: -8,
                left: -8,
                right: -8,
                bottom: -8,
                border: '2px solid rgba(86, 141, 237, 0.3)',
                borderRadius: '16px',
                transform: 'rotate(-2deg)',
              }} />
              <div style={{
                position: 'absolute',
                top: -4,
                left: -4,
                right: -4,
                bottom: -4,
                border: '1px solid rgba(86, 141, 237, 0.5)',
                borderRadius: '14px',
                transform: 'rotate(1deg)',
              }} />
              {/* Profile picture */}
              <div style={{
                position: 'relative',
                width: 140,
                height: 140,
                borderRadius: '12px',
                overflow: 'hidden',
                border: '3px solid rgba(86, 141, 237, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              }}>
                <img
                  src={profile.photoUrl || "/ex-profile.jpg"}
                  alt={profile.name || 'Employee'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>
            </div>

            {/* Name and basic info */}
            <div style={{ flex: 1 }}>
              <h2 style={{ 
                margin: 0,
                marginBottom: 8,
                fontFamily: 'Montserrat',
                fontSize: '28px',
                fontWeight: 700,
                color: '#FFFFFF',
              }}>
                {profile.name || emp.name || '—'}
              </h2>
              <div style={{ 
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'rgba(255, 255, 255, 0.8)',
                marginBottom: 12
              }}>
                {profile.roleTitle || emp.role || '—'}
                {(profile.department || emp.department) && (
                  <span style={{ opacity: 0.6 }}> • {profile.department || emp.department}</span>
                )}
              </div>
              {/* Status badge */}
              <div style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                backgroundColor: `${statusColor}20`,
                border: `1px solid ${statusColor}40`,
                borderRadius: '20px',
              }}>
                <span style={{ 
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: statusColor,
                }} />
                <span style={{ 
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#FFFFFF',
                }}>
                  {employeeStatus}
                </span>
              </div>
              {/* Employee ID */}
              {(meta.employeeNumber || emp.employeeId) && (
                <div style={{ 
                  marginTop: 12,
                  fontFamily: 'Montserrat',
                  fontSize: '13px',
                  color: 'rgba(255, 255, 255, 0.6)',
                }}>
                  ID: {meta.employeeNumber || emp.employeeId}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions Bar */}
        <QuickActions
          employeeId={employeeId}
          pendingPTOCount={pendingPTOCount}
          handleQuickAction={handleQuickAction}
        />

        {/* Employment Details */}
        {sectionTitle('fa-solid fa-briefcase', 'Employment Details')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {field('Start Date', profile.startDate || emp.startDate)}
          {field('Employment Type', (profile.employmentType || 'full_time').replace('_', ' ').toUpperCase())}
          {field('Manager', emp.managerName)}
          {field('Buddy/Mentor', profile.buddyMentor)}
          {probation.inProbation && field('Probation End', probation.endDate)}
          {location.country && field('Location', [location.city, location.country].filter(Boolean).join(', '))}
        </div>

        {/* Contact Information */}
        {sectionTitle('fa-solid fa-address-card', 'Contact Information')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {field('Work Email', profile.workEmail || contact.workEmail, true)}
          {field('Personal Email', profile.email || contact.personalEmail || contact.email, true)}
          {field('Phone', profile.phone || contact.personalPhone || contact.phone)}
          {field('Date of Birth', contact.dateOfBirth)}
        </div>

        {/* PTO / Time Off */}
        {sectionTitle('fa-solid fa-plane-departure', 'PTO / Time Off')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {field('Annual Leave Remaining', `${pto.balances?.annual?.remaining || 0} days`)}
          {field('Annual Leave Used', `${pto.balances?.annual?.used || 0} / ${pto.balances?.annual?.entitled || 0} days`)}
          {field('Sick Leave Remaining', `${pto.balances?.sick?.remaining || 0} days`)}
          {field('Unpaid Leave Used', `${pto.balances?.unpaid?.used || 0} days`)}
          {pto.nextReturnDate && field('Next Return Date', new Date(pto.nextReturnDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), true)}
        </div>

        {/* PTO Requests */}
        {Object.keys(pto.requests || {}).length > 0 && (
          <>
            {sectionTitle('fa-solid fa-calendar-check', 'PTO Requests')}
            {Object.entries(pto.requests || {}).map(([id, req]) => (
              <div key={id} style={{
                backgroundColor: 'rgba(86, 141, 237, 0.08)',
                border: '1px solid rgba(86, 141, 237, 0.15)',
                borderRadius: 12,
                padding: 20,
                marginBottom: 16
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div>
                    <div style={{
                      fontFamily: 'Montserrat',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#FFFFFF',
                      marginBottom: 4
                    }}>
                      {(req.type || 'vacation').charAt(0).toUpperCase() + (req.type || 'vacation').slice(1)} Leave
                    </div>
                    <div style={{
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      color: 'rgba(255, 255, 255, 0.7)'
                    }}>
                      {req.days || 0} days • {req.startDate} to {req.endDate}
                    </div>
                  </div>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    backgroundColor: req.status === 'pending' ? '#EBDD404D' : req.status === 'approved' ? '#40EB6E4D' : '#EB40404D',
                    border: `1px solid ${req.status === 'pending' ? '#EBDD4026' : req.status === 'approved' ? '#40EB6E26' : '#EB404026'}`,
                    borderRadius: '16px',
                    fontFamily: 'Montserrat',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    textTransform: 'capitalize'
                  }}>
                    {req.status || 'pending'}
                  </div>
                </div>
                {req.comment && (
                  <div style={{
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontStyle: 'italic'
                  }}>
                    "{req.comment}"
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {/* Performance */}
        {sectionTitle('fa-solid fa-chart-line', 'Performance')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {field('Current Rating', performance.currentRating ? `${performance.currentRating.toFixed(1)} / 5.0` : 'Not rated')}
          {field('Next Review Date', performance.nextReviewDate ? new Date(performance.nextReviewDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—')}
          {field('Total Reviews', Object.keys(performance.reviews || {}).length)}
          {field('Active Goals', Object.values(performance.goals || {}).filter(g => g.status !== 'completed').length)}
        </div>

        {/* Goals & Objectives */}
        {Object.keys(performance.goals || {}).length > 0 && (
          <>
            {sectionTitle('fa-solid fa-bullseye', 'Goals & Objectives')}
            {Object.entries(performance.goals || {}).map(([id, goal]) => (
              <div key={id} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: 16,
                backgroundColor: 'rgba(86, 141, 237, 0.08)',
                border: '1px solid rgba(86, 141, 237, 0.15)',
                borderRadius: 12,
                marginBottom: 12
              }}>
                <div style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: `2px solid ${goal.status === 'completed' ? '#40EB6E' : goal.status === 'in_progress' ? '#4048EB' : '#EBDD40'}`,
                  backgroundColor: goal.status === 'completed' ? '#40EB6E' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {goal.status === 'completed' && (
                    <i className="fa-solid fa-check" style={{ fontSize: 10, color: '#FFFFFF' }}></i>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontFamily: 'Montserrat',
                    fontSize: '15px',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    marginBottom: 4
                  }}>
                    {goal.title || goal.description || 'Goal'}
                  </div>
                  {goal.dueDate && (
                    <div style={{
                      fontFamily: 'Montserrat',
                      fontSize: '13px',
                      color: 'rgba(255, 255, 255, 0.7)'
                    }}>
                      Due: {new Date(goal.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Activity Timeline */}
        {sectionTitle('fa-solid fa-clock-rotate-left', 'Activity Timeline')}
        <ActivityTimeline activities={activities} loading={loadingActivities} />

        {/* Internal Notes */}
        {sectionTitle('fa-solid fa-sticky-note', 'Internal Notes')}
        <NotesSection
          ref={notesSectionRef}
          employeeId={employeeId}
          companyUID={companyUID}
          notes={notes}
          currentUser={currentUser}
          onAddNote={fetchNotes}
          loading={loadingNotes}
        />

        <div style={{ height: 40 }} />
      </div>

      {/* Modals */}
      {activeModal === 'email' && (
        <EmailComposer
          employeeId={employeeId}
          companyUID={companyUID}
          employeeName={profile.name || emp.name}
          employeeEmail={profile.workEmail || contact.workEmail || contact.email}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'schedule-review' && (
        <ScheduleReviewModal
          employeeId={employeeId}
          companyUID={companyUID}
          employeeName={profile.name || emp.name}
          employeeEmail={profile.workEmail || contact.workEmail || contact.email}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'approve-pto' && (
        <ApprovePTOModal
          employeeId={employeeId}
          companyUID={companyUID}
          employeeName={profile.name || emp.name}
          ptoRequests={pto.requests || {}}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {(activeModal === 'req-salary' || activeModal === 'req-bonus' || activeModal === 'req-offboarding' || activeModal === 'req-info') && (
        <ManagerRequestModal
          employeeId={employeeId}
          companyUID={companyUID}
          requestType={activeModal.replace('req-', '')}
          employeeName={profile.name || emp.name}
          managerId={managerId}
          managerName={managerName || currentUser?.displayName || 'Manager'}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
};

export default ManagerEmployeeDrawer;

